// Auth gate — shown when the bridge handshake fails AND there's no
// existing session cookie. OTP-only fallback path. Most customers never
// see this because the Liquid mount script provides a valid bridge.

import { useState } from 'react';
import { requestOtp, verifyOtp, fetchMe, StudioApiError } from '../lib/api';
import type { CustomerSummary } from '../lib/types';

interface AuthGateProps {
  onAuthenticated: (c: CustomerSummary) => void;
}

export function AuthGate({ onAuthenticated }: AuthGateProps) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const shopDomain =
    window.__MD_STUDIO_CONFIG__?.shopDomain ??
    (window.location.hostname.includes('marketdwellings.com')
      ? window.location.hostname
      : 'portal.marketdwellings.com');

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestOtp(email);
      setStep('code');
    } catch (err) {
      setError(translate(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyOtp({ email, code, shopDomain });
      const me = await fetchMe();
      onAuthenticated(me.customer);
    } catch (err) {
      setError(translate(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-gate">
      <div className="auth-gate__card">
        <p className="eyebrow" style={{ marginBottom: 8, color: 'var(--forest)' }}>
          Sign in
        </p>
        <h2 className="auth-gate__title">Open your studio</h2>
        <p className="auth-gate__sub">
          We&rsquo;ll email you a 6-digit code. No password needed.
        </p>
        {error && <p className="auth-gate__error">{error}</p>}

        {step === 'email' ? (
          <form onSubmit={onRequest}>
            <div className="auth-gate__field">
              <label htmlFor="auth-email">Email address</label>
              <input
                id="auth-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <button
              type="submit"
              className="btn btn--hero"
              style={{ width: '100%' }}
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Sending…' : 'Send me a code'}
            </button>
          </form>
        ) : (
          <form onSubmit={onVerify}>
            <div className="auth-gate__field">
              <label htmlFor="auth-code">6-digit code</label>
              <input
                id="auth-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
              />
            </div>
            <button
              type="submit"
              className="btn btn--hero"
              style={{ width: '100%' }}
              disabled={submitting || code.length !== 6}
              aria-busy={submitting}
            >
              {submitting ? 'Verifying…' : 'Open my studio'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              style={{ width: '100%', marginTop: 8 }}
              onClick={() => {
                setStep('email');
                setCode('');
                setError(null);
              }}
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function translate(err: unknown): string {
  if (err instanceof StudioApiError) {
    if (err.code === 'rate_limited') return 'Too many requests. Please wait a minute and try again.';
    if (err.code === 'locked') return 'Too many wrong attempts. Request a new code.';
    if (err.code === 'expired') return 'That code expired. Request a new one.';
    if (err.code === 'no_code' || err.code === 'wrong_code') return 'That code didn’t match. Try again.';
    if (err.code === 'no_customer_for_email') return 'We don’t see an account for that email yet.';
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}
